import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { AppContext } from '@/contexts/app-context';
import { useGtm } from '@/hooks/use-gtm';
import { useLogin } from '@/hooks/use-login';
import { useLocalization } from '@/hooks/use-localization';
import { validators } from '@/hooks/use-validation';
import { MapiError, useMapiApplication } from './use-mapi-application';
import { FieldsType, SupremeCardApplicationLabels, SupremeCardHasAccountResponse, SupremeCardOfferType, SupremeCardUpdatedOfferType } from './types';
import { ConsumerApplicationProduct, useDataLayer } from '@/hooks/use-data-layer';

type Props = {
    labels: SupremeCardApplicationLabels;
    defaultCard: string;
    hiddenCards?: string[];
    deferredSigningUrl: string;
    useMAPI?: boolean;
};

const PREFIX = 'supreme-card-application';

function getStorage<T>(key: string, defaultValue: T) {
    try {
        let item: string | boolean | number | T = sessionStorage?.getItem(`${PREFIX}-${key}`) ?? defaultValue;
        if (item === 'false') {
            item = false;
        }
        if (item === 'true') {
            item = true;
        }

        return (item as T) ?? defaultValue;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
        return defaultValue;
    }
}

function setStorage(key: string, value: string) {
    try {
        return sessionStorage?.setItem(`${PREFIX}-${key}`, value);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
        // No biggie
    }
}

const analyticsEvents: string[] = [];

export function useSupremeCardApplication({ labels, defaultCard, hiddenCards = [], deferredSigningUrl, useMAPI = false }: Props) {
    // Sorry about this, there doesn't seem to be a good way of checking
    // which type this response object is in TypeScript atm...
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isErrorType = (offer: any): offer is SupremeCardHasAccountResponse => typeof offer.message === 'string';

    const context = useContext(AppContext);
    const location = useLocation();
    const { sendPageInteraction } = useGtm();
    const { getLoginHintQuery } = useLogin();
    const { consumerApplicationFlowPush, dataLayerPush } = useDataLayer();
    const { initMapiLoginFlow, parseMapiErrors } = useMapiApplication({ labels });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errorTitle, setErrorTitle] = useState<string | null>(null);
    const [mapiError, setMapiError] = useState<MapiError | null>(null);
    const [offer, setOffer] = useState<SupremeCardOfferType | null>(null);
    const [updatedOffer, setUpdatedOffer] = useState<SupremeCardUpdatedOfferType | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
    const [shouldFocusError, setShouldFocusError] = useState(false);
    const { getLanguage, getLocale } = useLocalization();

    const getDefaultSelectedCard = () => {
        const query = queryString.parse(location.search) as Record<string, string>;

        if (query.selectedCard && !hiddenCards.includes(query.selectedCard)) {
            setStorage('selectedCard', query.selectedCard);
            return query.selectedCard;
        }

        // If we're returning from the signing / MAPI flow (login redirect),
        // preserve the in-flight selection that lives in sessionStorage so
        // the API call and analytics get the card the user actually picked.
        const isReturningFromSigningFlow = !!(query.confirmationStatus || query.mapiConfirmationStatus);
        if (isReturningFromSigningFlow) {
            const stored = getStorage<string>('selectedCard', defaultCard);
            if (hiddenCards.includes(stored)) {
                setStorage('selectedCard', '');
                return '';
            }
            return stored;
        }

        // Fresh navigation to a select-card page with no explicit
        // `?selectedCard=` query param: do NOT carry a previous card
        // selection across navigations. Reset to the WP-configured default
        // (which may be an empty string when the editor picked "No default card").
        // This fixes the bug where, after visiting a select-card page that set
        // `selectedCard` in sessionStorage, navigating to another select-card
        // page would still show the previously chosen card instead of resetting.
        if (hiddenCards.includes(defaultCard)) {
            setStorage('selectedCard', '');
            return '';
        }
        setStorage('selectedCard', defaultCard);
        return defaultCard;
    };

    function getValuesFromStorage() {
        return {
            selectedCard: getDefaultSelectedCard(),
            personalNumber: getStorage('personalNumber', ''),
            contactEmail: getStorage('contactEmail', ''),
            phoneMobile: getStorage('phoneMobile', ''),
            pepOptions: getStorage('pepOptions', ''),
            acceptTerms: false,
            amount: getStorage('amount', 0),
        };
    }

    const [step, setStep] = useState<1 | 2 | 3>(context.user?.isAuthenticated ? 2 : 1);
    const [fields, setFields] = useState<FieldsType>({
        selectedCard: '',
        personalNumber: '',
        contactEmail: '',
        phoneMobile: '',
        pepOptions: '',
        acceptTerms: false,
        amount: 0,
    });

    useEffect(() => {
        context.setSupremeCardApplication({ selectedCard: getDefaultSelectedCard() });
        setFields(getValuesFromStorage());
    }, []);

    const sendAnalyticsData = (data: Record<string, unknown>, applicationId: string | number = '') => {
        let selectedCard = fields.selectedCard;
        if (!selectedCard) {
            selectedCard = getValuesFromStorage().selectedCard;
        }

        if (!selectedCard) {
            return;
        }

        const args = {
            category: 'Supreme Card',
            typeOfCard: selectedCard,
            applicationID: applicationId,
            ...data,
        };

        const analyticsEvent = JSON.stringify(args);
        const existingEvent = analyticsEvents.find(event => event === analyticsEvent);
        if (existingEvent) {
            return;
        }

        analyticsEvents.push(analyticsEvent);
        sendPageInteraction(args);
    };

    useEffect(() => {
        const query = queryString.parse(location.search) as Record<string, string>;

        if (query?.mapiConfirmationStatus === 'success' || query?.mapiConfirmationStatus === 'failed') {
            const eventData = {
                ApplicationID: getStorage('applicationId', ''),
                ProductVariant: getStorage('selectedCard', ''),
                Product: ConsumerApplicationProduct['CREDIT_CARD'],
            };

            if (eventData.ApplicationID) {
                // Only send this data if there is an application ID.
                // This will always exist when the customer has created an application.
                dataLayerPush([
                    {
                        event: 'ApplicationSubmitted',
                        ...eventData,
                    },
                    {
                        event: 'ApplicationStatus',
                        Status: query?.mapiConfirmationStatus === 'success' ? 'Approved' : 'Rejected',
                        ...eventData,
                    },
                ]);
            }

            if (query?.mapiConfirmationStatus === 'success') {
                setStep(3);
                return;
            }

            setError(labels.mapiGenericErrorText || labels.errorGenericTitle);
            setStep(1);
            return;
        }

        if (!context.user) {
            return;
        }

        if (!query?.confirmationStatus && !offer) {
            getOffer();
            return;
        }

        if (query?.confirmationStatus === 'success') {
            setStep(3);
            registerSignedOffer();
        } else if (query?.confirmationStatus === 'postponed') {
            window.location.href = deferredSigningUrl;
        } else if (query?.confirmationStatus === 'failed') {
            sendAnalyticsData({
                status: 'form completed',
                decision: 'REJECTED',
            });
        } else {
            setError(labels.errorGenericTitle);
            setStep(1);
        }
    }, [location, context.user]);

    useEffect(() => {
        if (step === 1 && useMAPI) {
            const query = queryString.parse(location.search) as Record<string, string>;
            if (query?.mapiConfirmationStatus) {
                return;
            }

            dataLayerPush({
                event: 'ApplicationStart',
                Product: ConsumerApplicationProduct['CREDIT_CARD'],
                ProductVariant: query?.selectedCard || '',
            });

            return;
        }

        if (step === 1 && !context?.user?.isAuthenticated) {
            sendAnalyticsData({
                action: 'Step',
                label: 'Step 1',
            });
        }

        if (step !== 2 || !offer) {
            return;
        }

        sendAnalyticsData(
            {
                action: 'Step',
                label: 'Step 2',
                decision: offer?.application?.decision,
                value: offer?.application?.approvedLimit?.amount,
            },
            offer.application.applicationId || ''
        );
    }, [step, offer]);

    useEffect(() => {
        context.setSupremeCardApplication({ step });
    }, [step]);

    const updateField = (field: Partial<FieldsType>) => {
        setFields({ ...fields, ...field });

        for (const [key, value] of Object.entries(field)) {
            if (typeof value === 'string' || typeof value === 'number') {
                setStorage(key, `${value}`);
            } else {
                setStorage(key, JSON.stringify(value));
            }
        }
    };

    const getLoginUrl = (loginHint?: string) =>
        `/login?customRedirectUrl=${context.url}${location.pathname}&scope=supremeApplicationScope&loginHint=${loginHint}`;

    const validateFields = (): boolean => {
        const errors: Record<string, boolean> = {};

        if (!fields.selectedCard || hiddenCards.includes(fields.selectedCard)) {
            errors.selectCard = true;
        }
        if (
            !fields.personalNumber ||
            !validators.socialSecurityNumber(
                getLanguage() === 'sv' ? fields.personalNumber.replace(/[- ]+/, '') : fields.personalNumber,
                getLanguage()
            )
        ) {
            errors.personalNumber = true;
        }
        if (!fields.contactEmail || !validators.email(fields.contactEmail)) {
            errors.contactEmail = true;
        }
        if (!fields.phoneMobile || !validators.phoneMobile(fields.phoneMobile, getLocale())) {
            errors.phoneMobile = true;
        }
        if (!useMAPI && !fields.pepOptions) {
            errors.pepOptions = true;
        }
        if (!fields.acceptTerms) {
            errors.acceptTerms = true;
        }

        setFieldErrors(errors);
        const hasErrors = Object.keys(errors).length > 0;
        if (hasErrors) {
            setShouldFocusError(true);
        }
        return !hasErrors;
    };

    const onSubmit = async (step = 1) => {
        if (step === 1 && !validateFields()) {
            return;
        }

        if (useMAPI) {
            try {
                const response = await initMapiLoginFlow(fields.selectedCard, {
                    customerType: 'NATURAL',
                    email: fields.contactEmail,
                    mobilePhone: fields.phoneMobile,
                    governmentId: fields.personalNumber.replace(/\D+/g, ''),
                });

                consumerApplicationFlowPush({
                    event: 'ApplicationProgress',
                    ApplicationID: response.applicationId,
                    ProductVariant: fields.selectedCard,
                    BusinessVertical: 'B2C',
                    StepName: 'Login',
                    StepNumber: 1,
                });

                setStorage('applicationId', response.applicationId);
                if (response.redirectUrl) {
                    window.location.href = response.redirectUrl;
                }
            } catch (error) {
                if (error instanceof Error) {
                    setMapiError(error.cause as MapiError);
                    return;
                }

                setError(labels.errorGenericContent);
            }

            return;
        }

        if (step === 1) {
            sendAnalyticsData({
                action: 'Clicked button',
                label: 'Login and proceed',
            });

            const governmentId = fields.personalNumber.replace(/\D+/g, '');
            const { loginHint } = await getLoginHintQuery(governmentId);
            window.location.href = getLoginUrl(loginHint);
            return;
        }

        if (step === 2) {
            signOffer();
        }
    };

    const registerSignedOffer = async () => {
        try {
            const response = await fetch('/api/bank/supreme-card/apply/register', {
                method: 'PUT',
                headers: {
                    'content-type': 'application/json',
                    accept: 'application/json',
                    credentials: 'include',
                },
            });

            const data = await response.json();
            // const data = _getMockRegisterdOffer();

            sendAnalyticsData(
                {
                    status: 'form completed',
                    decision: data?.decision || 'APPROVED',
                    value: data?.selectedCreditLimit?.amount || '',
                    label: 'Step 3',
                },
                data.applicationId
            );
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            sendAnalyticsData({
                status: 'form completed',
                applicationID: '',
                decision: 'ERROR',
            });
        }
    };

    const getOfferType = () => {
        if (context?.user?.isAuthenticated !== true) {
            return '';
        }

        if (context?.user?.hasCardOfferRaise) {
            return 'SUPREME_CARD_LIMIT_RAISE';
        }

        return 'SUPREME_CARD_NEW';
    };

    const getApplyParams = (amount?: number) => {
        if (amount) {
            return {
                amount: {
                    value: amount,
                    currency: context.currency,
                },
                cardType: getStorage('selectedCard', defaultCard),
            };
        }

        return {
            isCustomer: context?.user?.isCustomer ?? false,
            cardType: getStorage('selectedCard', defaultCard),
            email: getStorage('contactEmail', ''),
            mobilePhoneNumber: getStorage('phoneMobile', ''),
            approveConditions: getStorage('acceptTerms', true),
            offerType: getOfferType(),
            isPep: getStorage('pepOptions', 'Ja') === 'Ja',
        };
    };

    const getOffer = async () => {
        setIsLoading(true);
        const data = getApplyParams();

        const response = await fetch('/api/bank/supreme-card/apply', {
            headers: {
                'content-type': 'application/json',
                credentials: 'include',
            },
            method: 'POST',
            body: JSON.stringify(data),
        });

        const offer = (await response.json()) as SupremeCardOfferType | SupremeCardHasAccountResponse;
        // const offer = _getMockOffer();

        setIsLoading(false);

        if (isErrorType(offer)) {
            sendAnalyticsData({
                action: 'Error',
                label: offer?.message || '',
            });
            setError(labels.errorGenericContent);

            return;
        }

        setOffer(offer);

        if (offer.decision === 'MANUAL_INSPECTION') {
            setErrorTitle(labels.manualInspectionTitle);
            setError(labels.manualInspectionContent);
            sendAnalyticsData({
                action: 'Error',
                label: 'Manual Inpection',
            });

            sendAnalyticsData(
                {
                    status: 'form completed',
                    decision: 'MANUAL_INSPECTION',
                },
                offer?.application?.applicationId ?? ''
            );
            return;
        }

        updateField({ amount: offer.maxLimit.amount });
    };

    const updateOffer = async (amount: number) => {
        const data = getApplyParams(amount);

        const response = await fetch('/api/bank/supreme-card/apply', {
            headers: {
                'content-type': 'application/json',
                accept: 'application/json',
                credentials: 'include',
            },
            method: 'PUT',
            body: JSON.stringify(data),
        });

        if (response.status !== 200) {
            setError(labels.errorGenericTitle);
            context.setSupremeCardApplication({ step: 1 });
            setStep(1);
            return;
        }

        const offer = (await response.json()) as SupremeCardUpdatedOfferType;
        // const offer = _getMockUpdatedOffer();

        if (offer) {
            sendAnalyticsData(
                {
                    action: 'Slider Completed',
                    label: 'Supreme Limit Slider',
                    value: amount,
                },
                offer?.applicationId
            );
            setUpdatedOffer(offer);
        }
    };

    const signOffer = async () => {
        const protocol = context.isProd ? 'https' : 'http';
        const url = `${protocol}://${context.host}`;

        setIsLoading(true);
        sendAnalyticsData({
            action: 'Clicked button',
            label: 'Sign application',
        });

        const successUrl = `${url}${location.pathname}?confirmationStatus=success`;
        const failUrl = `${url}${location.pathname}?confirmationStatus=failed`;
        const postponedUrl = `${url}${location.pathname}?confirmationStatus=postponed`;

        const response = await fetch('/api/bank/supreme-card/apply/sign', {
            headers: {
                'content-type': 'application/json',
                credentials: 'include',
            },
            method: 'POST',
            body: JSON.stringify({
                signatureOkUrl: successUrl,
                signatureCancelledUrl: failUrl,
                signaturePostponedUrl: postponedUrl,
            }),
        });

        const data = await response.json();
        setIsLoading(false);

        if (data.signRedirectUrl) {
            window.location.href = data.signRedirectUrl;
            return;
        }

        if (data?.confirmationStatus === 'failed' || data?.error === true || data?.statusCode !== 200) {
            setError(labels.errorGenericTitle);
        }

        if (data?.confirmationStatus === 'failed' || data?.confirmationStatus === 'postponed') {
            sendAnalyticsData({
                status: 'form completed',
                decision: data?.confirmationStatus === 'postponed' ? 'POSTPONED' : 'REJECTED',
            });
        }
    };

    return {
        step,
        setStep,
        fields,
        isLoading,
        updateField,
        updateOffer,
        onSubmit,
        offer,
        updatedOffer,
        error,
        mapiError,
        parseMapiErrors,
        errorTitle,
        fieldErrors,
        setFieldErrors,
        shouldFocusError,
        setShouldFocusError,
    };
}
